/**
 * Sales activities (meetings) — fieldiq-api `src/routes/sales-activities.routes.ts`
 * and `src/lib/sales-activity-write.ts` (CRM-API-2, api#1048; own-only soft
 * delete api#1053). Hand-written because the API manifest has not recorded
 * these routes.
 *
 * READS ARE camelCase; WRITES ARE snake_case. Both are the API's choice, not a
 * mistake to normalise here.
 */
export const SALES_ACTIVITY_KINDS = ['meeting', 'call', 'site_visit', 'other'] as const;
export type SalesActivityKind = (typeof SALES_ACTIVITY_KINDS)[number];

export interface SalesActivity {
  id: string;
  kind: string;
  /** An instant (timestamptz), ISO. */
  occurredAt: string;
  customerId: string;
  customerName: string | null;
  customerStatus: string | null;
  contactId: string | null;
  contactName: string | null;
  userId: string;
  ownerName: string | null;
  rigName: string | null;
  location: string | null;
  notes: string | null;
  nextStep: string | null;
  /** A calendar day 'YYYY-MM-DD', or null. */
  followUpDate: string | null;
  followUpDoneAt: string | null;
  opportunityId: string | null;
  source: string;
  /** null = a Scout draft. v1 never asks for drafts. */
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SalesActivityAttendee {
  id: string;
  userId: string | null;
  contactId: string | null;
  name: string | null;
  displayName: string;
}

export interface SalesActivityExpense {
  id: string;
  amount: number;
  status: string;
  paidBy: string;
}

export interface SalesActivityDetail extends SalesActivity {
  attendees: SalesActivityAttendee[];
  /** admin/office_staff/dispatcher see all; everyone else only their own. */
  expenses: SalesActivityExpense[];
  opportunity: { id: string; title: string; stage: string } | null;
}

export type SalesActivityAttendeeInput = { user_id: string } | { contact_id: string } | { name: string };

/** POST /sales-activities — snake_case. */
export interface SalesActivityCreateBody {
  customer_id: string;
  kind?: SalesActivityKind;
  occurred_at?: string;
  contact_id?: string | null;
  rig_name?: string | null;
  location?: string | null;
  notes?: string | null;
  next_step?: string | null;
  follow_up_date?: string | null;
  attendees?: SalesActivityAttendeeInput[];
  opportunity_id?: string | null;
}

/** PATCH /sales-activities/:id — partial; customer_id cannot change. */
export type SalesActivityUpdateBody = Partial<Omit<SalesActivityCreateBody, 'customer_id' | 'attendees'>> & {
  attendees?: SalesActivityAttendeeInput[] | null;
  follow_up_done?: boolean;
};

export interface SalesActivityListParams {
  customer_id?: string;
  mine?: 1;
  user_id?: string;
  from?: string;
  to?: string;
  follow_up?: 'open' | 'due' | 'overdue';
}
